import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Empty, Tag } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useAppStore } from "../../store";
import DataProvider from "../../apis";
import { formatCurrency } from "../../utils";
import Loader from "../elements/loader";

interface WithdrawalItem {
  id: number;
  amountRequested: number;
  dateRequested: string;
  status?: "pending" | "paid" | "confirmed" | "rejected";
}

const statusConfig: Record<string, { color: string; label: string }> = {
  pending: { color: "#94a3b8", label: "Pending" },
  paid: { color: "success", label: "Paid" },
  confirmed: { color: "processing", label: "Confirmed" },
  rejected: { color: "error", label: "Rejected" },
};

export default function Payouts() {
  const { doctor } = useAppStore();
  const [data, setData] = useState<WithdrawalItem[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!doctor) return;
    setLoading(true);
    try {
      const results = await DataProvider.getPayouts(doctor.id);
      setData(results.data ?? []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [doctor]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-5">
      <h3 className="m-0 text-sm font-semibold text-slate-800 tracking-tight">
        Payouts
      </h3>

      {loading && data === null ? (
        <div className="mt-3">
          <Loader num={3} />
        </div>
      ) : data && data.length > 0 ? (
        <ul className="m-0 mt-3 list-none space-y-2 p-0">
          {data.map((item) => {
            const status = statusConfig[item.status ?? ""] ?? {
              color: "#94a3b8",
              label: item.status ?? "—",
            };
            return (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3"
              >
                <div className="min-w-0">
                  <p className="m-0 font-medium text-slate-800 flex items-center gap-1.5">
                    <CalendarOutlined className="text-slate-400" />
                    {moment(item.dateRequested).format("Do MMM, YYYY")}
                  </p>
                  <Tag color={status.color} className="mt-1 text-xs rounded">
                    {status.label}
                  </Tag>
                </div>
                <p className="m-0 shrink-0 font-semibold text-slate-700">
                  {formatCurrency(item.amountRequested)}
                </p>
              </li>
            );
          })}
        </ul>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span className="text-gray-500">No payouts available.</span>
          }
          className="py-8"
        />
      )}
    </div>
  );
}
